import * as React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { CheckIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { ItemList, itemVariants, type ItemListSearch } from "@/components/ui/item";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";

/**
 * Pick one of a list: a radio group drawn as ItemList rows (arrow keys move,
 * like any radio group), the picked row marked only by a primary check (no
 * background — rows only tint on hover). Rows take
 * ItemMedia / ItemContent / ItemTitle / ItemDescription as children; the
 * className goes on the list (e.g. `max-h-100` to scroll); `search` adds
 * ItemList's search field above the rows.
 *
 * @example
 * import { useState } from "react"
 * import { ItemContent, ItemDescription, ItemTitle } from "@/components/ui/item"
 * import { SelectList, SelectListItem } from "@/components/ui/select-list"
 *
 * type Campaign = { id: string; name: string; platform: string }
 *
 * export function PickCampaign({ campaigns }: { campaigns: Campaign[] }) {
 *   const [selected, setSelected] = useState("")
 *   return (
 *     <SelectList value={selected} onValueChange={setSelected} className="max-h-80">
 *       {campaigns.map((c) => (
 *         <SelectListItem key={c.id} value={c.id}>
 *           <ItemContent className="min-w-0 gap-0">
 *             <ItemTitle className="truncate">{c.name}</ItemTitle>
 *             <ItemDescription className="capitalize">{c.platform}</ItemDescription>
 *           </ItemContent>
 *         </SelectListItem>
 *       ))}
 *     </SelectList>
 *   )
 * }
 */
function SelectList({
  className,
  search,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> & { search?: ItemListSearch }) {
  return (
    <ItemList className={className} search={search}>
      <RadioGroupPrimitive.Root
        data-slot="select-list"
        className="flex flex-col gap-0.5"
        {...props}
      />
    </ItemList>
  );
}

function SelectListItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item>) {
  return (
    <ScopeWrapper className="block">
      <RadioGroupPrimitive.Item
        data-slot="select-list-item"
        data-variant="row"
        className={cn(
          itemVariants({ variant: "row" }),
          "w-full flex-nowrap text-left hover:bg-muted focus-visible:ring-inset disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        {...props}
      >
        {children}
        <RadioGroupPrimitive.Indicator className="ml-auto flex size-5 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
          <CheckIcon className="size-3.5" strokeWidth={2.5} />
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
    </ScopeWrapper>
  );
}

export { SelectList, SelectListItem };
